import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import clsx from 'clsx';
import { Field, Form, Formik, FormikProps } from 'formik';
import identity from 'lodash/identity';
import pickBy from 'lodash/pickBy';
import { IDataTableFilters, IDataTableRef } from 'src/components/DataTable/shared/types';
import TradeMarksField from 'src/components/Fields/TradeMarksField';
import SegmentedControl from 'src/components/SegmentedControl/SegmentedControl';
import { vendorUrlRegexPath, withPimURL } from 'src/helpers/service';
import { cloneObject, isEqual, omit, pick } from 'src/helpers/utils';
import { translate } from 'src/locales/i18next/config';
import Translations from 'src/locales/i18next/translations/translation';
import Container from 'src/pages/inventory/components/category-container';
import { flashProductsFilterValuesInitialValues, flashProductsFilterValuesValidationSchema, IFlashProductsFilterValues } from '../helpers/constants';
import { downloadURI, flashProductsFilterSerializer } from '../helpers/helper';
import useFlashDiscountPeriodTypes from '../services/hooks/use-flash-discount-period-types';
import { useHistory } from 'react-router-dom';

let outerCategories: any[] = [];

interface IFlashProductsFilterProps {
  dataTableRef: React.RefObject<IDataTableRef>;
}

export default function FlashProductsFilter(props: IFlashProductsFilterProps) {
  const { dataTableRef } = props;

  const [filters, setFilters] = useState<IDataTableFilters>(flashProductsFilterValuesInitialValues);

  const [updatedCategory, setUpdatedCategory] = useState<any>([]);
  const formikRef = useRef<FormikProps<IFlashProductsFilterValues>>(null);

  const t = translate(Translations);
  const translation = 'pages.flashProducts.list.filter';

  const flashDiscountPeriodTypes = useFlashDiscountPeriodTypes();
  const history = useHistory();

  const periodTypeOptions = useMemo(() => {
    const options = flashDiscountPeriodTypes.data?.map((period) => ({
      value: String(period.id),
      label: period.title,
    }));

    return [
      {
        value: null,
        label: 'Tümü',
      },
      ...(options || []),
    ] as { value: number | null; label: string }[];
  }, [flashDiscountPeriodTypes.data]);

  const findValue = (arr: any, val: any, checked: any) => {
    for (let obj of arr) {
      if (obj.id === val) {
        obj.checked = checked;
        return arr;
      }
      if (obj.children) {
        let result: any = findValue(obj.children, val, checked);
        if (result) {
          return arr;
        }
      }
    }
    return undefined;
  };

  const onChangeCategoryFunc = (currentNode: any, selectedNodes: any) => {
    var cpyUpdatedCategory = [...updatedCategory];
    outerCategories = selectedNodes?.map((i: any) => i.id);
    formikRef.current?.setFieldValue('categoryId', outerCategories);
    setUpdatedCategory(findValue(cpyUpdatedCategory, currentNode.id, currentNode.checked));
  };

  const generateUpdateCategories = useCallback((data: any[], selectedIds = []): any[] => {
    return data?.map((category: any) => ({
      id: category?.id,
      label: category?.name,
      value: category?.name,
      children: category?.subs?.length ? generateUpdateCategories(category?.subs) : [],
      checked: selectedIds?.includes(category?.id),
    }));
  }, []);

  const disableForm = useMemo(() => {
    return flashDiscountPeriodTypes.status !== 'success';
  }, [flashDiscountPeriodTypes.status]);

  const getCategoryQueryKey = useMemo(() => ['get-categories'], []);

  const getCategories = useQuery({
    queryKey: getCategoryQueryKey,
    queryFn: async () => {
      const response = await axios.get(withPimURL(vendorUrlRegexPath(':vendorId/category/tree/list'))).then((res) => res.data);
      setUpdatedCategory(generateUpdateCategories(response || []));
      return response;
    },
    enabled: true,
    refetchOnWindowFocus: false,
  });

  const handleFilterChange = (values: Partial<IFlashProductsFilterValues>) => {
    const trimmedValues = Object.entries(values).reduce((acc: any, [key, value]) => {
      acc[key] = typeof value === 'string' ? value.trim() : value;
      return acc;
    }, {});

    const newFiltersDispatch = (prev: IDataTableFilters) => {
      return {
        ...prev,
        ...trimmedValues,
      };
    };

    setFilters(newFiltersDispatch);
    dataTableRef?.current?.setFilters(newFiltersDispatch);
  };

  const handleSubmit = (_values: IFlashProductsFilterValues) => {
    const values = Object.assign({}, _values);

    if (values.periodTypeId) {
      delete (values as any).periodTypeId;
    }

    handleFilterChange(values);
  };

  const filterIsChanged = useCallback(
    (
      filterValue?: any,
      deleteInObj: ('tab' | 'page' | 'sort')[] = ['tab', 'page', 'sort'],
      onlyControl?: (keyof IFlashProductsFilterValues)[],
      doNotControl?: (keyof IFlashProductsFilterValues)[],
    ) => {
      if (!filterValue) filterValue = filters;

      const cloneFilterValue = cloneObject(filterValue);
      const cloneInitialFilterValues = cloneObject(flashProductsFilterValuesInitialValues);

      (deleteInObj || []).forEach((key) => {
        delete cloneFilterValue[key];
        delete cloneInitialFilterValues[key];
      });

      let a = cloneInitialFilterValues;
      let b = cloneFilterValue;

      if (onlyControl) {
        a = pick(cloneInitialFilterValues, onlyControl as string[]);
        b = pick(cloneFilterValue, onlyControl as string[]);
      }

      if (doNotControl) {
        a = omit(cloneInitialFilterValues, doNotControl as string[]);
        b = omit(cloneFilterValue, doNotControl as string[]);
      }

      return !isEqual(a, b);
    },
    [filters],
  );

  const addToExportQueueWithExcel = useMutation({
    mutationKey: ['flash-products-add-to-export-queue-with-excel', filters],
    mutationFn: () => {
      const serializedFilters = flashProductsFilterSerializer(filters);
      const params = pickBy(serializedFilters, identity);

      return axios
        .get(withPimURL(vendorUrlRegexPath('/flash-discount/:vendorId/export')), {
          params: {
            ...params,
          },
        })
        .then((res) => res.data);
    },
    onSuccess: (response: any) => {
      excelQueueController.mutate(response?.id);
    },
  });

  const excelQueueController: any = useMutation({
    mutationKey: ['flas-products-excel-queue-controller', addToExportQueueWithExcel?.data?.id || null],
    mutationFn: async (ID: string | number) => {
      const res = await axios
        .get(withPimURL(vendorUrlRegexPath('vendor/:vendorId/export-list')), {
          params: {
            exportType: 'flash_discount',
          },
        })
        .then((res) => res.data);

      const response = res?.find((item: any) => item.id.toString() === ID.toString() && item.state === 'completed');
      if (!response) throw new Error('Tablonuz henüz hazır değil');
      return response || {};
    },
    onSuccess(data: any) {
      if (data?.downloadUrl) {
        downloadURI(data?.downloadUrl);
      }

      addToExportQueueWithExcel.reset();
      excelQueueController.reset();
    },
    retry: true,
    /*retry(failureCount, error: any) {
      if (error?.message === 'Tablonuz henüz hazır değil' && failureCount < 2) return true;
      return failureCount < 4;
    },*/
    retryDelay: 10000,
  });

  const excelFileIsExporting = useMemo(() => {
    return addToExportQueueWithExcel.isLoading || excelQueueController.isLoading;
  }, [addToExportQueueWithExcel.isLoading, excelQueueController.isLoading]);

  return (
    <>
      <Formik initialValues={flashProductsFilterValuesInitialValues} onSubmit={handleSubmit} innerRef={formikRef} validationSchema={flashProductsFilterValuesValidationSchema}>
        {({ values, setFieldValue, resetForm, isValid, errors, touched, handleBlur }) => (
          <Form className="form">
            <div className="row align-items-start">
              {/** Left Side */}
              <div className="col-md-9">
                <div className="row">
                  <div className="mb-5 col-md-4">
                    <Field
                      id="searchTerm"
                      name="searchTerm"
                      type="text"
                      value={values.searchTerm || ''}
                      className={clsx('form-control form-control-solid', { 'is-invalid': errors.searchTerm && touched.searchTerm })}
                      placeholder="Ürün Adı, Stok Kodu, Barkod, Ürün Kodu"
                      disabled={disableForm}
                    />
                    {errors.searchTerm && touched.searchTerm && <div className="invalid-feedback position-absolute -bottom-20">{errors.searchTerm}</div>}
                  </div>

                  <div className="mb-5 col-md-4">
                    <Container
                      keepTreeOnSearch
                      clearSearchOnChange={true}
                      texts={{ placeholder: 'Kategori seçiniz', noMatches: 'Arama sonucu bulunamadı' }}
                      className={`w-100 pb-0 pt-0 pl-0 ps-0 category`}
                      onChange={onChangeCategoryFunc}
                      data={updatedCategory ? updatedCategory : []}
                      disabled={disableForm}
                    />
                    {errors.categoryId && touched.categoryId && <div className="invalid-feedback position-absolute -bottom-20">{errors.categoryId}</div>}
                  </div>

                  <div className="mb-5 col-md-4">
                    <TradeMarksField
                      fetchFromMCAPI
                      searchFromMCAPI
                      queryKey="flash-products-brand"
                      defaultValue={values.brandId as any}
                      placeholder="Marka"
                      hideSelectedOptions={true}
                      onChange={(value) => setFieldValue('brandId', value)}
                      isMulti
                      className={clsx('', { 'is-invalid': errors.brandId && touched.brandId })}
                      isDisabled={disableForm}
                      styles={{
                        control: (baseStyles, state) => ({
                          ...baseStyles,
                          backgroundColor: '#F5F8FA',
                          borderWidth: 0,
                          width: '100%',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          flexWrap: 'nowrap',
                          alignItems: 'center',
                        }),
                      }}
                    />
                    {errors.brandId && touched.brandId && <div className="invalid-feedback position-absolute -bottom-20">{errors.brandId}</div>}
                  </div>
                </div>
              </div>

              {/** Right Side */}
              <div className="col-md-3">
                <div className="row d-flex justify-content-center mb-5">
                  <div className="col-md-6 col-lg-6 col-sm-6">
                    <button
                      type="submit"
                      className="btn btn-light-primary w-100"
                      disabled={
                        (disableForm ||
                          (!filterIsChanged(values) && isEqual(omit(filters, ['periodTypeId']), omit(values, ['periodTypeId']))) ||
                          (filterIsChanged(values) && isEqual(omit(filters, ['periodTypeId']), omit(values, ['periodTypeId'])))) as boolean
                      }
                      title={!filterIsChanged(values) ? 'Filtreler değiştirilmedi' : undefined}
                    >
                      {t(`${translation}.search`)}
                    </button>
                  </div>
                  <div className="col-md-6 col-lg-6 col-sm-6">
                    <button
                      type="button"
                      onClick={() => {
                        resetForm();
                        handleFilterChange(flashProductsFilterValuesInitialValues);
                        setUpdatedCategory(generateUpdateCategories(getCategories.data || []));
                      }}
                      className="btn btn-secondary w-100"
                      disabled={!filterIsChanged(values)}
                      title={!filterIsChanged(values) ? 'Filtreler değiştirilmedi' : undefined}
                    >
                      {t(`${translation}.clean`)}
                    </button>
                  </div>
                </div>

                <div className="row d-flex justify-content-center mb-5">
                  <button
                    type="button"
                    className="btn btn-light-success w-100"
                    onClick={() => {
                      const excelQueueId = addToExportQueueWithExcel?.data?.id || null;
                      if (!excelQueueId) {
                        addToExportQueueWithExcel.mutate();
                      } else {
                        excelQueueController.mutate(excelQueueId);
                      }
                    }}
                    disabled={excelFileIsExporting}
                  >
                    {excelFileIsExporting && <span className="spinner-border spinner-border-sm align-middle me-2"></span>}
                    {t(`${translation}.downloadExcel`)}
                  </button>
                </div>

                <div className="row d-flex justify-content-center mb-5">
                  <button
                    type="button"
                    className="btn btn-light-primary w-100"
                    onClick={() => {
                      history.push('/products/add-products/bulk-options', {
                        preselectedDocumentType: 'flash_discount',
                      });
                    }}
                  >
                    {t(`${translation}.bulkAddFlashProducts`)}
                  </button>
                </div>
              </div>
            </div>

            <div className="row mt-5">
              <div className="col-md-12 d-flex align-items-center justify-content-end">
                <SegmentedControl
                  disabled={disableForm}
                  options={periodTypeOptions}
                  forceValue={filters.periodTypeId}
                  onValueChange={(value) => handleFilterChange({ periodTypeId: value ? String(value) : null })}
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
